<template lang="pug">
ValidationObserver(
  ref="dialogObserver"
  slim
)
  AppDialog.wow(
    :class="{ opened: show, 'put-under': clientDialogOpen, edit: editing }"
    :title="`${editing ? 'Edit' : 'New'} work order`"
    show
    append-to-body
    @close="closeWizard"
  )
    form#work-order-form(@submit.prevent="onFormSubmit")
      AppWizard(
        :value="step"
        :editing="editing"
        :steps="workOrderSteps()"
        @next="nextStep"
        @previous="previousStep"
        @tab-selected="changeTab"
      )
        AppWizardStep
          template(#footer)
            .hidden
          ValidationObserver(
            ref="createStep"
            vid="createObs"
            slim
          )
            CreateStep(
              v-show="step === 0"
              :form="form"
              :editing="editing"
              :select2-options="select2Options"
              :grouped-clients="groupedClients"
              @toggle-client-dialog="clientDialogOpen = $event"
            )
        AppWizardStep
          template(#footer)
            .hidden
          ValidationObserver(
            ref="siteStep"
            vid="siteObs"
            slim
          )
            WorkOrderSiteStep(
              v-show="step === 1"
              ref="siteStepComponent"
              :form="form"
              :editing="editing"
              :visible="step === 1"
              :readonly="isImmutableJobSite"
            )

        template(v-if="editing && canManageBudget")
          AppWizardStep
            template(#footer)
              .hidden
            ValidationObserver(
              ref="budgetStep"
              vid="budgetObs"
              slim
            )
              WorkOrdersBudgetStep(
                v-show="step === 2"
                ref="budgetStepComponent"
                :form="form"
                :organization="organization"
                :duration-format="durationFormat"
                :has-budget="!!editingWorkOrder.has_budget"
                @reset-budget="onBudgetStateReset"
                @update-budget="onBudgetStateUpdate"
              )

    template(#footer)
      template(v-if="!editing")
        AppButton.btn-default.pull-left(@click="closeWizard") Cancel
        div(v-if="step === 0")
          button.btn.hs-btn-transparent-primary.save-exit-link(
            type="submit"
            form="work-order-form"
            :disabled="!isFormValid || form.processing"
            @click="saveAction = 'save-exit'"
          ) Save and close
          AppButton(
            type="primary"
            :disabled="!isFormValid"
            @click="nextStep"
          ) Next
        div(v-else)
          AppButton(
            type="default"
            @click="previousStep"
          ) Back
          button.btn.btn-primary.total-save-link(
            type="submit"
            form="work-order-form"
            :disabled="!isFormValid || form.processing"
            @click="saveAction = 'save-total'"
          ) Save
      template(v-else)
        .d-flex.justify-content-between.flex-1
          AppButton(
            type="default"
            @click="closeWizard"
          ) Cancel
          button.btn.btn-primary(
            type="submit"
            form="work-order-form"
            :disabled="!isFormValid || form.processing"
            @click="saveAction = 'save-total'"
          ) Save
</template>

<script>
import { get } from 'lodash-es';
import CreateStep from './CreateStep.vue';
import { mapInertiaProps } from '~/utils/inertia';
import { JOB_SITE_ACTIONS, WORK_ORDERS_URL } from '~/app_constants';
import WorkOrdersBudgetStep from './WorkOrdersBudgetStep.vue';
import WorkOrderSiteStep from '../jobs_dialog/WorkOrderSiteStep.vue';
import DialogToasterMixin from '~/mixins/WorkOrders/DialogToasterMixin';
import { prepareBudgetPayload, prepareJobSitePayload } from '~/stores/WorkOrderStore/common/utils';
import { applyTemplate } from '~/utils/url_template';

export default {
  name: 'WorkOrdersDialog',
  components: {
    CreateStep,
    WorkOrderSiteStep,
    WorkOrdersBudgetStep,
  },
  mixins: [
    DialogToasterMixin({
      updateMessage: 'Work order successfully updated',
      createMessage: 'Work order successfully created',
    }),
  ],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: Number,
      default: 0,
    },
    editingWorkOrder: {
      type: Object,
      default: () => ({}),
    },
    groupedClients: {
      type: Array,
      required: true,
    },
    canManageBudget: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const jobSite = get(this.editingWorkOrder, 'jobSite', null);
    const budget = this.editing ? this.editingWorkOrder.budget : null;

    return {
      step: 0,
      saveAction: null,
      budgetState: {},
      clientDialogOpen: false,
      hasResetBudget: false,
      isFormValid: false,
      budgetHasError: false,
      form: this.$inertia.form({
        id: this.editing ? this.editingWorkOrder.id : null,
        // Create Step
        number: this.editing ? this.editingWorkOrder.number : null,
        client_id: this.editing ? this.editingWorkOrder.client.id : null,
        instructions: this.editing ? this.editingWorkOrder.instructions : null,
        // Site Step
        radius: get(jobSite, 'radius', null),
        address: get(jobSite, 'address', null),
        position: {
          lat: get(jobSite, 'position.lat', null),
          lng: get(jobSite, 'position.lng', null),
        },
        enter_action: get(jobSite, 'enter_action', JOB_SITE_ACTIONS.doNothing),
        exit_action: get(jobSite, 'exit_action', JOB_SITE_ACTIONS.doNothing),
        ...// Budget Step
        // We only care about the budget in editing mode
        (!this.editing
          ? {}
          : {
              type: budget.type,
              rate: budget.rate,
              alert: budget.alert,
              stop_timer: budget.stop_timer,
              cost: budget.cost || null,
              recurrence: budget.recurrence,
              include_non_billable: budget.include_non_billable,
              start: budget.start ? moment(budget.start) : null,
              hours_display: budget.hours ? budget.hours * 60 * 60 : null,
            }),
      }),
    };
  },
  computed: {
    ...mapInertiaProps({
      clients: 'clients',
      organization: 'organization',
      jobSiteMinimumRadius: 'job_site_minimum_radius',
      jobSiteDefaultRadius: 'job_site_default_radius',
      durationFormat: ({ current_user: user }) => user.duration_format,
    }),
    select2Options() {
      return {
        width: '100%',
      };
    },
    isImmutableJobSite() {
      return this.editing && this.editingWorkOrder && this.editingWorkOrder.job_site_immutable;
    },
  },
  watch: {
    async 'form.client_id'(newValue) {
      if (this.editing) {
        return;
      }

      const client = newValue && newValue !== 'add-client'
        ? this.clients.find((client) => client.id === newValue)
        : null;

      if (client?.address) {
        // Client has a default location → prefill it (the site step geocodes it on entry).
        this.form.address = client.address;
      } else {
        // No default location for this client → clear the job-site fields in the step so the
        // previous client's address/coordinates don't linger (which would block Save).
        this.$refs.siteStepComponent?.resetJobSite?.();
      }
    },
    // vee-validate v4's meta.valid starts `true` (no errors yet), unlike v3 which
    // silently flagged empty required fields as invalid. Recompute the aggregate
    // validity (across all step observers) on any change via silent validation —
    // this disables Next/Save without surfacing error messages prematurely.
    form: {
      deep: true,
      handler() {
        this.refreshValidity();
      },
    },
    step() {
      this.refreshValidity();
    },
  },
  mounted() {
    this.form.radius = this.form.radius || this.jobSiteDefaultRadius || this.jobSiteMinimumRadius;

    if (typeof this.defaultTab === 'number') {
      this.step = this.defaultTab;
    }

    this.refreshValidity();
  },
  methods: {
    workOrderSteps() {
      const steps = [
        { title: this.editing ? 'General' : 'Create', id: 'createStep' },
        { title: 'Job site', id: 'siteStep' },
        ...(this.editing && this.canManageBudget ? [{ title: 'Budget', id: 'budgetStep' }] : []),
      ];

      return steps.map((step) => {
        // The budget step's fields live in BudgetsForm's nested observers, which the
        // budgetStep observer doesn't aggregate — track its error state separately.
        if (step.id === 'budgetStep') {
          return { ...step, hasError: this.budgetHasError };
        }

        const observer = get(this.$refs, step.id);
        const hasError = !!(observer && observer.flags.dirty && observer.flags.invalid);

        return {
          ...step,
          hasError,
        };
      });
    },
    closeWizard(id = null) {
      this.$emit('close', id);
    },

    /**
     * Wizard steps navigation
     */
    changeTab(tabId) {
      const id = Number(tabId.replace('tab-', ''));

      this.step = id;
    },
    nextStep() {
      this.step++;
    },
    previousStep() {
      this.step--;
    },
    async refreshValidity() {
      await this.$nextTick();

      // mode: 'silent' computes validity without mutating fields (no error messages
      // shown) — matches vee-validate v3's pre-interaction behavior.
      const checks = [
        this.$refs.createStep?.validate?.({ mode: 'silent' }),
        this.$refs.siteStep?.validate?.({ mode: 'silent' }),
      ];

      // The budget step nests its fields in BudgetsForm's own observers, so validate
      // through the component (the budgetStep observer has no fields of its own).
      // Only gate Save on the budget once it's been started (one of type/based-on/cost
      // filled) — an all-empty budget is optional and must NOT block Save.
      if (this.editing && this.canManageBudget && this.isBudgetStarted()) {
        checks.push(this.$refs.budgetStepComponent?.validateSilently?.());
      }

      const results = await Promise.all(checks.map((check) => Promise.resolve(check ?? true)));

      this.isFormValid = results.every(Boolean) && this.hasValidJobSiteLocation();
    },
    // Block Save until the job site has real coordinates: only a Google-Maps-selected
    // address sets form.position (lat/lng); typing arbitrary text clears it (see
    // WorkOrderSiteStep.onAddressInput). Only enforced on the site step (create, step 1) —
    // at step 0 the address isn't entered yet, and during edit it's read-only.
    hasValidJobSiteLocation() {
      if (this.editing || this.step !== 1) {
        return true;
      }

      const position = this.form.position;

      return !!(position && typeof position.lat === 'number' && typeof position.lng === 'number');
    },

    /**
     * Form handling
     */
    // The budget is "started" once any of its three core fields is filled. Validation
    // only runs while started — an all-empty budget is optional (no errors, no red,
    // doesn't block Save).
    isBudgetStarted() {
      const filled = (value) => value !== null && value !== undefined && value !== '';

      return filled(this.form.type) || filled(this.form.rate) || filled(this.form.cost);
    },
    async onBudgetStateUpdate() {
      this.hasResetBudget = false;

      await this.$nextTick();

      if (this.isBudgetStarted()) {
        // Interacting with one budget field runs the whole step's validation
        // (non-silent → surfaces "can't be blank" on the still-empty required fields)
        // and reds the Budget step.
        this.budgetHasError = this.$refs.budgetStepComponent
          ? !(await this.$refs.budgetStepComponent.validate())
          : false;
      } else {
        // Back to all-empty → drop any surfaced errors and the tab's error state.
        this.$refs.budgetStepComponent?.reset?.();
        this.budgetHasError = false;
      }
    },
    onBudgetStateReset() {
      this.hasResetBudget = true;
      this.$refs.budgetStepComponent?.reset?.();
      this.budgetHasError = false;
    },
    onFormSubmit() {
      const { origin } = window.location;
      const url = new URL(
        applyTemplate(`${WORK_ORDERS_URL}`, {
          organizationId: this.organization.id,
          woId: this.editing ? this.editingWorkOrder.id : null,
        }),
        origin,
      );
      const workOrdersUrl = url.toString();
      const action = this.saveAction;

      this.form
        // This will mutate the underlying form data that we're
        // going to send to the server.
        .transform((data) => {
          const { client_id: clientId, number, instructions } = data;
          const payload = { number, instructions, client_id: clientId };

          if (action === 'save-total') {
            // Job site payload
            prepareJobSitePayload(payload, data);

            if (this.editing) {
              // Add the id for the work order
              payload.id = this.editingWorkOrder.id;
              // Budget payload
              prepareBudgetPayload(payload, this.hasResetBudget, data);
            }
          }

          return { work_order: payload };
        });

      const options = { onSuccess: () => this.closeWizard() };

      if (this.editing) {
        // Check if the user has reset the budget and if so,
        // make sure that it was the intended action.
        // If not, just cancel the update.
        if (
          this.hasResetBudget &&
          !confirm("Removing this work order's budget will remove its budget history. Are you sure?")
        ) {
          return;
        }

        return this.form.put(workOrdersUrl, options);
      }

      return this.form.post(workOrdersUrl, options);
    },
  },
};
</script>
